import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { apiClient } from '@/lib/axios';
import type { ApiResponse, PaginationMeta } from '@vayu-saathi/shared-types';
import type {
  ExpenseCategory,
  ExpenseClaim,
  ExpenseStatusFilter,
  ExpenseSummary,
} from '@/lib/expenses';

export interface ExpenseListParams {
  status?: ExpenseStatusFilter | undefined;
  employeeId?: string | undefined;
  month?: number | undefined;
  year?: number | undefined;
  page?: number | undefined;
  limit?: number | undefined;
}

export interface ExpenseListResult {
  claims: ExpenseClaim[];
  meta: PaginationMeta | undefined;
}

export const expenseKeys = {
  all: ['expenses'] as const,
  my: (params: ExpenseListParams) => ['expenses', 'my', params] as const,
  list: (params: ExpenseListParams) => ['expenses', 'list', params] as const,
  detail: (id: string) => ['expenses', 'detail', id] as const,
  summary: ['expenses', 'summary'] as const,
};

function clean(params: ExpenseListParams): Record<string, string | number> {
  const out: Record<string, string | number> = {};
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== '' && v !== null) out[k] = v as string | number;
  }
  return out;
}

/** The caller's own claims, newest first (GET /expenses/my). */
export function useMyExpenses(params: ExpenseListParams = {}, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: expenseKeys.my(params),
    enabled: options?.enabled ?? true,
    queryFn: async (): Promise<ExpenseListResult> => {
      const res = await apiClient.get<ApiResponse<ExpenseClaim[]>>('/expenses/my', {
        params: clean(params),
      });
      return { claims: res.data.data, meta: res.data.meta };
    },
    // Receipt URLs are signed for 30 min — don't serve them from a stale cache
    staleTime: 60_000,
    meta: { errorMessage: 'Could not load your claims' },
  });
}

/** HR / Admin / Owner: claims across the org (GET /expenses). */
export function useExpenses(params: ExpenseListParams = {}, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: expenseKeys.list(params),
    enabled: options?.enabled ?? true,
    queryFn: async (): Promise<ExpenseListResult> => {
      const res = await apiClient.get<ApiResponse<ExpenseClaim[]>>('/expenses', {
        params: clean(params),
      });
      return { claims: res.data.data, meta: res.data.meta };
    },
    staleTime: 60_000,
    meta: { errorMessage: 'Could not load reimbursement claims' },
  });
}

/** Every page of GET /expenses for the given filters — used for CSV export. */
export async function fetchAllExpenses(
  params: Omit<ExpenseListParams, 'page' | 'limit'>,
): Promise<ExpenseClaim[]> {
  const out: ExpenseClaim[] = [];
  const limit = 100;
  for (let page = 1; page <= 50; page++) {
    const res = await apiClient.get<ApiResponse<ExpenseClaim[]>>('/expenses', {
      params: clean({ ...params, page, limit }),
    });
    out.push(...res.data.data);
    const meta = res.data.meta;
    if (!meta || !meta.hasNextPage || res.data.data.length === 0) break;
  }
  return out;
}

export function useExpense(id: string | null) {
  return useQuery({
    queryKey: expenseKeys.detail(id ?? ''),
    enabled: !!id,
    queryFn: async () => {
      const res = await apiClient.get<ApiResponse<ExpenseClaim>>(`/expenses/${id ?? ''}`);
      return res.data.data;
    },
    // Always refetch on open so receipt links are freshly signed; links last ~30 min, so
    // re-sign every 25 min while the sheet stays open
    staleTime: 0,
    refetchInterval: 25 * 60_000,
    meta: { errorMessage: 'Could not load this claim' },
  });
}

/** Reviewers get approval counts; `scope: 'my'` asks for the caller's own claim counts (any role). */
export function useExpenseSummary(options?: { enabled?: boolean; scope?: 'my' }) {
  const scope = options?.scope;
  return useQuery({
    queryKey: [...expenseKeys.summary, scope ?? 'default'] as const,
    enabled: options?.enabled ?? true,
    queryFn: async () => {
      const res = await apiClient.get<ApiResponse<ExpenseSummary>>('/expenses/summary', {
        params: scope ? { scope } : undefined,
      });
      return res.data.data;
    },
    refetchInterval: 60_000,
    // A dashboard card / nav badge — a failure here shouldn't pop a toast
    meta: { suppressErrorToast: true },
  });
}

export interface NewExpenseInput {
  clientId: string;
  category: ExpenseCategory;
  amount: number;
  expenseDate: string;
  description: string;
  receipts: File[];
}

function invalidateExpenseViews(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: expenseKeys.all });
  void qc.invalidateQueries({ queryKey: ['approval-inbox'] });
}

export function useCreateExpense() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: NewExpenseInput) => {
      // Text fields first, then files — the API reads the multipart stream in order
      const form = new FormData();
      form.append('clientId', input.clientId);
      form.append('category', input.category);
      form.append('amount', input.amount.toFixed(2));
      form.append('expenseDate', input.expenseDate);
      form.append('description', input.description);
      for (const f of input.receipts) form.append('receipts', f, f.name);
      const res = await apiClient.post<ApiResponse<ExpenseClaim>>('/expenses', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120_000,
      });
      return res.data.data;
    },
    onSuccess: () => {
      invalidateExpenseViews(qc);
      toast.success('Claim submitted — HR will review it');
    },
    meta: { errorMessage: 'Could not submit the claim' },
  });
}

export function useApproveExpense() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, remarks }: { id: string; remarks?: string | undefined }) => {
      const res = await apiClient.post<ApiResponse<ExpenseClaim>>(
        `/expenses/${id}/approve`,
        remarks?.trim() ? { remarks: remarks.trim() } : {},
      );
      return res.data.data;
    },
    onSuccess: (claim) => {
      invalidateExpenseViews(qc);
      toast.success(
        claim.status === 'APPROVED'
          ? 'Claim approved — it will be paid with the next salary'
          : 'Claim approved — sent to the Owner for final approval',
      );
    },
    meta: { errorMessage: 'Could not approve the claim' },
  });
}

export function useRejectExpense() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, remarks }: { id: string; remarks: string }) => {
      const res = await apiClient.post<ApiResponse<ExpenseClaim>>(`/expenses/${id}/reject`, {
        remarks: remarks.trim(),
      });
      return res.data.data;
    },
    onSuccess: () => {
      invalidateExpenseViews(qc);
      toast.success('Claim rejected — the employee has been notified');
    },
    meta: { errorMessage: 'Could not reject the claim' },
  });
}

export function useCancelExpense() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await apiClient.post<ApiResponse<ExpenseClaim>>(`/expenses/${id}/cancel`);
      return res.data.data;
    },
    onSuccess: () => {
      invalidateExpenseViews(qc);
      toast.success('Claim cancelled');
    },
    meta: { errorMessage: 'Could not cancel the claim' },
  });
}
